import { useGuardedDefaultValues } from "@msuite/ui/hooks";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import {
	type AcknowledgementDefinitionFormSchema,
	acknowledgementDefinitionSchema,
} from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { AcknowledgementDefinitionForm } from "@/forms/acknowledgement-definition-form";
import { client } from "@/hono";
import { ModalLoadFailed } from "@/components/modals";

/** Props Interface */
interface EditAcknowledgementDefinitionModalProps extends ModalProps {
	acknowledgementDefinitionId: string;
	onComplete?: () => Promise<void>;
}

export function EditAcknowledgementDefinitionModal({
	isOpen,
	onClose,
	acknowledgementDefinitionId,
	onComplete,
}: EditAcknowledgementDefinitionModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const { defaultValues, loadFailed } = useGuardedDefaultValues(async () => {
		const response = await client.organizations["acknowledgement-definitions"][
			":acknowledgementDefinitionId"
		].$get({
			param: { acknowledgementDefinitionId },
		});
		const data = await responseHandler(response);
		return acknowledgementDefinitionSchema.parse(data.acknowledgementDefinition);
	});
	const methods = useForm<AcknowledgementDefinitionFormSchema>({
		resolver: resolver(acknowledgementDefinitionSchema),
		defaultValues,
	});

	/** Functions */
	async function handleOnSubmit(json: AcknowledgementDefinitionFormSchema) {
		const response = await client.organizations["acknowledgement-definitions"][
			":acknowledgementDefinitionId"
		].$patch({
			param: { acknowledgementDefinitionId },
			json,
		});
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				title={t("organization.worksite_acknowledgement_definitions.edit")}
				isOpen={isOpen}
				onClose={onClose}
				onSubmit={methods.handleSubmit(handleOnSubmit, console.error)}
				isLoading={methods.formState.isLoading && !loadFailed}
			>
				{loadFailed ? (
					<ModalLoadFailed onClose={onClose} />
				) : (
					<>
						<Modal.Body>
							<AcknowledgementDefinitionForm />
						</Modal.Body>
						<Modal.FooterForm />
					</>
				)}
			</Modal>
		</FormProvider>
	);
}
